import { useState, useEffect, useRef, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, ArrowUp, ArrowDown, Check, ChevronDown } from 'lucide-react';
import IconRenderer from '../ui/IconRenderer';
import useNavLayoutStore, { UNGROUPED_ID } from '../../stores/useNavLayoutStore';
import './NavOrganizer.css';

/**
 * NavOrganizer — the "Customise nav" panel behind the `navGroups` module.
 *
 * The sidebar ships FLAT and header-less. When a user enables `navGroups` in the
 * Store, the sidebar header grows a customise control that opens this panel;
 * here they create named groups and file nav items into them. Anything they
 * don't file stays in the implicit ungrouped bucket that renders last, with no
 * header — so an enabled-but-empty module is indistinguishable from no module.
 *
 * KEYBOARD, NOT DRAG
 *   Every operation has a real <button>: move a group up/down, move an item
 *   up/down inside its group, and a "Move to" disclosure that lists every group
 *   plus Unfiled. There is no pointer-only path anywhere in this panel.
 *
 *   The "Move to" menu is an INLINE disclosure, not an absolutely-positioned
 *   popover: the panel body scrolls (`overflow-y: auto`), which would clip an
 *   absolute dropdown. Expanding in flow keeps it visible and scroll-into-view
 *   -able at every panel height.
 *
 * PORTAL
 *   Rendered into document.body. The sidebar transforms itself (the zen-hide
 *   slide), and a transformed ancestor becomes the containing block for
 *   `position: fixed` — an in-place overlay would be trapped inside the 252px
 *   rail. Same reasoning as Tooltip.
 *
 * @param {boolean}  open      whether the panel is shown
 * @param {Function} onClose   called on Escape, backdrop click, or Done
 * @param {Array}    items     the VISIBLE nav items — `{ path, label, icon }`.
 *                             `icon` is a lucide NAME string (as in the sidebar's
 *                             NAV data), rendered via IconRenderer.
 */
export default function NavOrganizer({ open, onClose = () => {}, items = [] }) {
    const groups = useNavLayoutStore(s => s.groups);
    const createGroup = useNavLayoutStore(s => s.createGroup);
    const renameGroup = useNavLayoutStore(s => s.renameGroup);
    const deleteGroup = useNavLayoutStore(s => s.deleteGroup);
    const reorderGroups = useNavLayoutStore(s => s.reorderGroups);
    const moveItem = useNavLayoutStore(s => s.moveItem);

    const panelRef = useRef(null);
    const newInputRef = useRef(null);
    const titleId = useId();
    const descId = useId();

    const [newLabel, setNewLabel] = useState('');
    // Uncommitted rename text, keyed by group id. Committed on blur / Enter so a
    // momentarily-empty field doesn't get rejected mid-typing.
    const [drafts, setDrafts] = useState({});
    // Path of the row whose "Move to" disclosure is expanded (only one at a time).
    const [openMenuPath, setOpenMenuPath] = useState(null);

    // Mirror for the keydown handler, which is attached once per open.
    const openMenuRef = useRef(null);
    openMenuRef.current = openMenuPath;

    // Reset transient UI whenever the panel opens.
    useEffect(() => {
        if (!open) return;
        setNewLabel('');
        setDrafts({});
        setOpenMenuPath(null);
    }, [open]);

    // ── Focus trap: move focus in, cycle Tab inside, restore on close ──
    useEffect(() => {
        if (!open) return undefined;
        const panel = panelRef.current;
        if (!panel) return undefined;

        const previouslyFocused = document.activeElement;

        // Everything inside the panel is visible by construction (the panel only
        // exists while open, and nothing here is display:none), so the selector
        // is the whole filter. Deliberately NOT gated on `offsetParent`: that is
        // null for every element under jsdom, which would empty the list.
        const focusables = () =>
            Array.from(
                panel.querySelectorAll(
                    'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
                )
            ).filter(el => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');

        // Land on the first control rather than the panel itself.
        focusables()[0]?.focus();

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                // An open "Move to" disclosure swallows the first Escape.
                if (openMenuRef.current) {
                    setOpenMenuPath(null);
                    return;
                }
                onClose();
                return;
            }
            if (e.key !== 'Tab') return;
            const list = focusables();
            if (list.length === 0) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        panel.addEventListener('keydown', handleKeyDown);
        return () => {
            panel.removeEventListener('keydown', handleKeyDown);
            // Return focus to whatever opened the panel (the header control).
            if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
                previouslyFocused.focus();
            }
        };
    }, [open, onClose]);

    // path → item, so a group can render its filed items without re-scanning.
    const byPath = useMemo(() => {
        const map = new Map();
        for (const item of items) {
            if (item && typeof item.path === 'string' && item.path && !map.has(item.path)) {
                map.set(item.path, item);
            }
        }
        return map;
    }, [items]);

    const claimed = useMemo(() => {
        const set = new Set();
        for (const g of groups) for (const p of g.itemPaths) set.add(p);
        return set;
    }, [groups]);

    const ungrouped = useMemo(
        () => items.filter(i => !(i && typeof i.path === 'string' && claimed.has(i.path))),
        [items, claimed]
    );

    const groupIdForPath = (path) => {
        const owner = groups.find(g => g.itemPaths.includes(path));
        return owner ? owner.id : UNGROUPED_ID;
    };

    if (!open) return null;

    const handleAddGroup = (e) => {
        e.preventDefault();
        const label = newLabel.trim();
        if (!label) {
            newInputRef.current?.focus();
            return;
        }
        createGroup(label);
        setNewLabel('');
        newInputRef.current?.focus();
    };

    const commitRename = (id) => {
        const draft = drafts[id];
        if (draft !== undefined) renameGroup(id, draft);
        setDrafts(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    /** The inline "Move to" disclosure shared by grouped and unfiled rows. */
    const renderMoveMenu = (item) => {
        const path = item.path;
        const currentId = groupIdForPath(path);
        const currentLabel =
            currentId === UNGROUPED_ID
                ? 'Unfiled'
                : (groups.find(g => g.id === currentId)?.label ?? 'Unfiled');
        const expanded = openMenuPath === path;

        return (
            <div className="nav-organizer__move">
                <button
                    type="button"
                    className="btn btn-tertiary btn-sm btn-icon nav-organizer__move-trigger"
                    aria-expanded={expanded}
                    aria-label={`Move ${item.label} to a group. Currently in ${currentLabel}`}
                    onClick={() => setOpenMenuPath(expanded ? null : path)}
                >
                    <span className="nav-organizer__move-value">{currentLabel}</span>
                    <ChevronDown
                        className={`nav-organizer__move-caret ${expanded ? 'nav-organizer__move-caret--open' : ''}`}
                        size={14}
                        aria-hidden="true"
                    />
                </button>

                {expanded && (
                    <ul className="nav-organizer__move-list animate-fade-in">
                        {[...groups.map(g => ({ id: g.id, label: g.label })), { id: UNGROUPED_ID, label: 'Unfiled' }]
                            .map(option => {
                                const selected = option.id === currentId;
                                return (
                                    <li key={option.id}>
                                        <button
                                            type="button"
                                            className={`nav-organizer__move-option ${selected ? 'nav-organizer__move-option--selected' : ''}`}
                                            aria-pressed={selected}
                                            onClick={() => {
                                                moveItem(path, option.id === UNGROUPED_ID ? null : option.id);
                                                setOpenMenuPath(null);
                                            }}
                                        >
                                            <span>{option.label}</span>
                                            {selected && <Check size={14} aria-hidden="true" />}
                                        </button>
                                    </li>
                                );
                            })}
                    </ul>
                )}
            </div>
        );
    };

    const renderItemRow = (item, { groupId = null, index = 0, total = 0 } = {}) => (
        <li className="nav-organizer__item" key={item.path || item.label}>
            <span className="nav-organizer__item-icon" aria-hidden="true">
                <IconRenderer icon={{ type: 'lucide', value: item.icon }} size={18} fallback="•" />
            </span>
            <span className="nav-organizer__item-label">{item.label}</span>

            {groupId && (
                <span className="nav-organizer__item-order">
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn"
                        aria-label={`Move ${item.label} up`}
                        disabled={index === 0}
                        onClick={() => moveItem(item.path, groupId, index - 1)}
                    >
                        <ArrowUp size={14} aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn"
                        aria-label={`Move ${item.label} down`}
                        disabled={index >= total - 1}
                        onClick={() => moveItem(item.path, groupId, index + 1)}
                    >
                        <ArrowDown size={14} aria-hidden="true" />
                    </button>
                </span>
            )}

            {item.path ? renderMoveMenu(item) : (
                <span className="nav-organizer__item-fixed">Always last</span>
            )}
        </li>
    );

    return createPortal(
        <div
            className="nav-organizer__overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="nav-organizer"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                ref={panelRef}
            >
                <header className="nav-organizer__header">
                    <h2 className="nav-organizer__title" id={titleId}>Customise navigation</h2>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn nav-organizer__close"
                        onClick={onClose}
                        aria-label="Close customise navigation"
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </header>

                <p className="nav-organizer__hint" id={descId}>
                    Group the sidebar however you think. Anything you leave unfiled sits at
                    the bottom, without a header — exactly as it does today.
                </p>

                <div className="nav-organizer__body">
                    <form className="nav-organizer__add" onSubmit={handleAddGroup}>
                        <label className="nav-organizer__add-label" htmlFor={`${titleId}-new`}>
                            New group name
                        </label>
                        <div className="nav-organizer__add-row">
                            <input
                                id={`${titleId}-new`}
                                ref={newInputRef}
                                className="nav-organizer__input"
                                type="text"
                                value={newLabel}
                                placeholder="e.g. Morning"
                                onChange={(e) => setNewLabel(e.target.value)}
                            />
                            <button type="submit" className="btn btn-primary nav-organizer__add-btn">
                                <Plus size={15} aria-hidden="true" />
                                <span>Add group</span>
                            </button>
                        </div>
                    </form>

                    {groups.map((group, gIndex) => (
                        <section className="nav-organizer__group" key={group.id}>
                            <div className="nav-organizer__group-head">
                                <label className="nav-organizer__sr-only" htmlFor={`${titleId}-g-${group.id}`}>
                                    Group name
                                </label>
                                <input
                                    id={`${titleId}-g-${group.id}`}
                                    className="nav-organizer__input nav-organizer__group-name"
                                    type="text"
                                    value={drafts[group.id] ?? group.label}
                                    onChange={(e) => setDrafts(prev => ({ ...prev, [group.id]: e.target.value }))}
                                    onBlur={() => commitRename(group.id)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn"
                                    aria-label={`Move group ${group.label} up`}
                                    disabled={gIndex === 0}
                                    onClick={() => reorderGroups(gIndex, gIndex - 1)}
                                >
                                    <ArrowUp size={14} aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn"
                                    aria-label={`Move group ${group.label} down`}
                                    disabled={gIndex >= groups.length - 1}
                                    onClick={() => reorderGroups(gIndex, gIndex + 1)}
                                >
                                    <ArrowDown size={14} aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm btn-icon nav-organizer__icon-btn"
                                    aria-label={`Remove group ${group.label}. Its items move back to unfiled`}
                                    onClick={() => deleteGroup(group.id)}
                                >
                                    <Trash2 size={14} aria-hidden="true" />
                                </button>
                            </div>

                            {(() => {
                                const filed = group.itemPaths.map(p => byPath.get(p)).filter(Boolean);
                                if (filed.length === 0) {
                                    return (
                                        <p className="nav-organizer__empty">
                                            Empty for now — use “Move to” on any item below.
                                        </p>
                                    );
                                }
                                return (
                                    <ul className="nav-organizer__items">
                                        {filed.map((item, i) =>
                                            renderItemRow(item, { groupId: group.id, index: i, total: filed.length })
                                        )}
                                    </ul>
                                );
                            })()}
                        </section>
                    ))}

                    <section className="nav-organizer__group nav-organizer__group--ungrouped">
                        <div className="nav-organizer__group-head nav-organizer__group-head--static">
                            <h3 className="nav-organizer__group-title">Unfiled</h3>
                            <span className="nav-organizer__group-note">renders last, no header</span>
                        </div>
                        {ungrouped.length === 0 ? (
                            <p className="nav-organizer__empty">Everything is filed.</p>
                        ) : (
                            <ul className="nav-organizer__items">
                                {ungrouped.map(item => renderItemRow(item))}
                            </ul>
                        )}
                    </section>
                </div>

                <footer className="nav-organizer__footer">
                    <button type="button" className="btn btn-primary nav-organizer__done" onClick={onClose}>
                        Done
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    );
}
